import { Tooltip, TooltipContent, TooltipTrigger } from '@monksflow/monks-ui';
import {
  CONNECTOR_PLATFORM_ICONS,
  CONNECTOR_PLATFORM_LABELS,
  type ConnectorPlatform,
} from '../../model/types';

export interface PlatformLogoProps {
  /** A platform slug. Anything unrecognised falls back to `fallbackLabel`. */
  platform: string | null | undefined;
  /**
   * What to show when the platform has no logo of its own - a manual campaign, or a
   * connector that has since been deleted. Distinct situations the caller already knows
   * how to name, so this component does not guess.
   */
  fallbackLabel: string;
  className?: string;
}

/**
 * A platform's logo, with its name on hover.
 *
 * The name is never dropped, only moved: it stays the image's alternative text and the
 * tooltip's content, so the column reads the same to a screen reader as it did when it
 * was a text column. A platform with no logo keeps its label as text rather than
 * rendering a blank cell.
 *
 * Assumes an ambient `TooltipProvider` from an ancestor, like every other tooltip in
 * this app - the root mounts none.
 */
export function PlatformLogo({ platform, fallbackLabel, className }: PlatformLogoProps) {
  const hasLogo = Boolean(platform) && Object.hasOwn(CONNECTOR_PLATFORM_ICONS, platform!);

  if (!hasLogo) {
    return <span className="text-sm text-muted-foreground">{fallbackLabel}</span>;
  }

  const key = platform as ConnectorPlatform;
  const label = CONNECTOR_PLATFORM_LABELS[key];

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <img
          src={CONNECTOR_PLATFORM_ICONS[key]}
          alt={label}
          className={className ?? 'size-5 shrink-0 rounded-sm object-contain'}
        />
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}
